import React, { FormEvent, ReactElement, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Input, Loader } from "@screencloud/screencloud-ui-components";
import sha256 from "crypto-js/sha256";
// import Debug from "debug";
import { Stream, AuthMode, ViewerStore, ErrorState } from "../../types";
import { RootState } from "../../reducers";
import { verifyPassword } from "../../actions";
import LoginButton, { LoginButtonType } from "../LoginButton/LoginButton";
import "./Auth.scss";
import ErrorMessage from "../ErrorMessage/ErrorMessage";

// const debug = Debug("broadcast:components:Auth");

interface AuthProps {
  stream: Stream;
  buttonType: LoginButtonType;
  className?: string;
}

const Auth = (props: AuthProps): ReactElement<AuthProps> => {
  const dispatch = useDispatch();
  const viewer: ViewerStore = useSelector((state: RootState) => state.viewer);

  const { stream, buttonType, className } = props;

  const [password, setPassword] = useState("");

  const sendPassword = (): void => {
    if (viewer.channel !== null) {
      const { channel } = viewer;
      const { screenCloudOrgId: orgId, spaceId, channelId, arn } = channel;
      if (password.length > 0) {
        dispatch(
          verifyPassword({
            orgId,
            spaceId,
            channelId,
            arn,
            password: sha256(password).toString(),
          })
        );
      }
    }
  };

  return (
    <div className={`stream-authentication ${className || ""}`}>
      <div className="stream-name">{stream.name}</div>
      <div className="stream-auth-label">
        This stream requires{" "}
        <span className="capitalize">{stream.authMode}</span> authentication to
        view.
      </div>
      {(stream.authMode === AuthMode.Slack ||
        stream.authMode === AuthMode.Teams) && (
        <>
          <div className="stream-auth-label">
            Please log in with your{" "}
            <span className="capitalize">{stream.authMode}</span> credentials
            for the <b>{stream ? stream.orgName : ""}</b> workspace.
          </div>
          {viewer.loading ? (
            <Loader
              size="small"
              active
              inline="centered"
              className="auth-spinner"
              disabled={viewer.loading}
            />
          ) : (
            <LoginButton
              authMode={stream.authMode}
              buttonType={buttonType}
              usageType={"viewer"}
            />
          )}
        </>
      )}
      {stream.authMode === AuthMode.Password && (
        <div className="password-auth">
          <div className="stream-auth-label" />
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              sendPassword();
            }}
          >
            <Input
              className="password-textbox"
              type="password"
              placeholder="Password"
              autoComplete="off"
              value={password}
              onChange={(_, data) => {
                setPassword(data.value);
              }}
            />
          </form>
          <Button
            primary
            className="login-button"
            onClick={() => {
              sendPassword();
            }}
          >
            {viewer.loading ? (
              <Loader
                size="small"
                active
                inline="centered"
                className="auth-spinner"
                disabled={viewer.loading}
              />
            ) : (
              <span>Log in</span>
            )}
          </Button>
          {viewer.errors.length > 0 &&
            viewer.errors.map((error) =>
              error.error === ErrorState.FailedToVerifyPassword ? (
                <ErrorMessage key={error.uuid} error={error} />
              ) : (
                <div />
              )
            )}
        </div>
      )}
    </div>
  );
};

export default Auth;
